/**
 * Strict SemVer 2.0.0 (https://semver.org) without dependencies, shared by the server (display of the newest allowed
 * Desktop version) and the Desktop app (the authoritative update decision). Exactly the official grammar: no leading
 * "v", no leading zeros in numeric identifiers, build metadata is accepted but ignored for precedence.
 */
export interface SemVer {
  major: number;
  minor: number;
  patch: number;
  /** Dot-separated prerelease identifiers ([] for a release version). */
  prerelease: readonly (string | number)[];
}

// The regular expression published at semver.org (numbered capture groups).
const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/** Longest version string accepted anywhere (keeps inputs bounded). */
export const MAX_SEMVER_LENGTH = 64;

export function parseSemver(input: unknown): SemVer | null {
  if (typeof input !== "string" || input.length === 0 || input.length > MAX_SEMVER_LENGTH) return null;
  const m = SEMVER_RE.exec(input);
  if (!m) return null;
  const nums = [m[1], m[2], m[3]].map(Number);
  if (nums.some((n) => !Number.isSafeInteger(n))) return null;
  const prerelease = m[4] ? m[4].split(".").map((id) => (/^\d+$/.test(id) ? Number(id) : id)) : [];
  if (prerelease.some((id) => typeof id === "number" && !Number.isSafeInteger(id))) return null;
  return { major: nums[0]!, minor: nums[1]!, patch: nums[2]!, prerelease };
}

export const isValidSemver = (input: unknown): input is string => parseSemver(input) !== null;

/** True for `1.2.3-beta.1`, false for `1.2.3` and for anything that is not valid SemVer. */
export function isPrereleaseSemver(input: string): boolean {
  const v = parseSemver(input);
  return v !== null && v.prerelease.length > 0;
}

function compareIdentifiers(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a === b ? 0 : a < b ? -1 : 1;
  if (typeof a === "number") return -1; // numeric identifiers have lower precedence than alphanumeric ones
  if (typeof b === "number") return 1;
  return a === b ? 0 : a < b ? -1 : 1; // ASCII order, as required by the spec
}

/** SemVer precedence: negative if a < b, 0 if equal, positive if a > b. Throws for invalid input. */
export function compareSemverStrict(a: string, b: string): number {
  const va = parseSemver(a);
  const vb = parseSemver(b);
  if (!va || !vb) throw new Error("invalid SemVer version");
  for (const k of ["major", "minor", "patch"] as const) {
    if (va[k] !== vb[k]) return va[k] < vb[k] ? -1 : 1;
  }
  const pa = va.prerelease;
  const pb = vb.prerelease;
  if (pa.length === 0 || pb.length === 0) return pa.length === pb.length ? 0 : pa.length === 0 ? 1 : -1;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if (i >= pa.length) return -1;
    if (i >= pb.length) return 1;
    const c = compareIdentifiers(pa[i]!, pb[i]!);
    if (c !== 0) return c;
  }
  return 0;
}

export type UpdateChannelName = "stable" | "beta";

export interface UpdateCandidate {
  channel: UpdateChannelName;
  version: string;
}

/**
 * THE Beta/Stable rule set (single implementation; the Desktop's `selectUpdate` delegates here):
 *  - only versions STRICTLY higher than the installed one qualify (never a downgrade, never the same version);
 *  - Beta off: only the stable feed counts; Beta on: both feeds, the highest SemVer wins;
 *  - the same version in both feeds resolves to the stable feed;
 *  - a prerelease advertised in the STABLE feed is a publishing mistake and is never offered;
 *  - candidates with an invalid version are ignored.
 * Throws when the installed version is not valid SemVer (the caller must not guess).
 */
export function selectUpdateCandidate<T extends UpdateCandidate>(input: {
  installed: string;
  receiveBetaUpdates: boolean;
  candidates: readonly T[];
}): T | null {
  const installed: string = input.installed;
  if (!isValidSemver(installed))
    throw new Error(`Installed version "${String(input.installed)}" is not valid SemVer`);
  let best: T | null = null;
  for (const c of input.candidates) {
    if (!isValidSemver(c.version)) continue;
    if (c.channel === "beta" && !input.receiveBetaUpdates) continue;
    if (c.channel === "stable" && isPrereleaseSemver(c.version)) continue;
    if (compareSemverStrict(c.version, installed) <= 0) continue;
    if (!best) best = c;
    else {
      const cmp = compareSemverStrict(c.version, best.version);
      if (cmp > 0 || (cmp === 0 && c.channel === "stable")) best = c;
    }
  }
  return best;
}
